import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'ds-radio',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <label
      class="ds-radio-option"
      [class.ds-radio-option--selected]="selected"
      [class.ds-radio-option--disabled]="effectiveDisabled"
      [attr.for]="inputId"
    >
      <input
        class="ds-radio__input"
        type="radio"
        [attr.id]="inputId"
        [attr.name]="effectiveName"
        [value]="value"
        [checked]="selected"
        [disabled]="effectiveDisabled"
        [required]="effectiveRequired"
        [attr.aria-label]="label ? null : ariaLabel"
        [attr.aria-describedby]="effectiveAriaDescribedBy"
        (change)="select()"
        (focus)="focused.emit($event)"
        (blur)="blurred.emit($event)"
      />

      <span class="ds-radio" aria-hidden="true">
        <span class="ds-radio__indicator">
          <span class="ds-radio__dot"></span>
        </span>
      </span>

      @if (label) {
        <span class="ds-radio__label">{{ label }}</span>
      }
    </label>
  `,
  styles: [`
    :host {
      display: inline-flex;
    }

    .ds-radio-option {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: var(--ds-radio-label-gap);
      color: var(--ds-radio-label-color);
      cursor: pointer;
    }

    .ds-radio-option--disabled {
      opacity: calc(var(--ds-radio-disabled-opacity) / 100px);
      cursor: not-allowed;
    }

    .ds-radio__input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      opacity: 0;
      cursor: inherit;
    }

    .ds-radio {
      width: var(--ds-radio-container-size);
      height: var(--ds-radio-container-size);
      display: inline-grid;
      place-items: center;
      padding: 0;
      border: 0 solid transparent;
      border-radius: var(--ds-radio-container-radius);
      background: transparent;
      box-sizing: border-box;
      transition:
        background-color var(--ds-radio-transition-duration) ease,
        border-color var(--ds-radio-transition-duration) ease,
        border-width var(--ds-radio-transition-duration) ease;
    }

    .ds-radio-option:not(.ds-radio-option--disabled):hover .ds-radio {
      border-width: var(--ds-radio-hover-border-width);
      border-color: var(--ds-radio-hover-border-color);
      background: var(--ds-radio-hover-background);
    }

    .ds-radio__input:focus-visible + .ds-radio {
      border-width: var(--ds-radio-focus-border-width);
      border-color: var(--ds-radio-focus-border-color);
      outline: none;
    }

    .ds-radio__indicator {
      width: var(--ds-radio-size);
      height: var(--ds-radio-size);
      display: inline-grid;
      place-items: center;
      border: var(--ds-radio-border-width) solid var(--ds-radio-border-color);
      border-radius: var(--ds-radio-border-radius);
      background: var(--ds-radio-background);
      box-sizing: border-box;
    }

    .ds-radio__dot {
      width: var(--ds-radio-dot-size);
      height: var(--ds-radio-dot-size);
      border-radius: var(--ds-radio-border-radius);
      background: var(--ds-radio-dot-color);
      opacity: 0;
      transform: scale(0);
      transition:
        opacity var(--ds-radio-transition-duration) ease,
        transform var(--ds-radio-transition-duration) ease;
    }

    .ds-radio-option--selected .ds-radio__dot {
      opacity: 1;
      transform: scale(1);
    }

    .ds-radio__label {
      font-size: var(--ds-radio-label-font-size);
      font-weight: var(--ds-radio-label-font-weight);
      line-height: var(--ds-radio-label-line-height);
      padding-inline-end: var(--ds-radio-label-gap);
    }
  `],
})
export class DsRadioComponent {
  private static counter = 0;

  readonly inputId = `ds-radio-${++DsRadioComponent.counter}`;

  @Input() selected = false;
  @Input() disabled = false;
  @Input() ariaLabel = 'Radio';
  @Input() ariaDescribedBy: string | null = null;
  @Input() label = '';
  @Input() name: string | null = null;
  @Input() required = false;
  @Input() value = '';

  @Output() selectedChange = new EventEmitter<boolean>();
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();

  private groupAriaDescribedBy: string | null = null;
  private groupDisabled = false;
  private groupName: string | null = null;
  private groupRequired = false;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  get effectiveAriaDescribedBy(): string | null {
    return this.groupAriaDescribedBy ?? this.ariaDescribedBy;
  }

  get effectiveDisabled(): boolean {
    return this.disabled || this.groupDisabled;
  }

  get effectiveName(): string | null {
    return this.groupName ?? this.name;
  }

  get effectiveRequired(): boolean {
    return this.required || this.groupRequired;
  }

  select(): void {
    if (this.effectiveDisabled) return;
    this.selected = true;
    this.selectedChange.emit(true);
  }

  setGroupState(state: {
    ariaDescribedBy: string | null;
    disabled: boolean;
    name: string;
    required: boolean;
    selected: boolean;
  }): void {
    this.groupAriaDescribedBy = state.ariaDescribedBy;
    this.groupDisabled = state.disabled;
    this.groupName = state.name;
    this.groupRequired = state.required;
    this.selected = state.selected;
    this.changeDetectorRef.markForCheck();
  }
}
